import React, { createContext, FC, ReactElement, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { lazy } from 'yup';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import Details from 'buyside/components/presentation/IdeaForm/Details/details';
import Disclaimers from 'buyside/components/presentation/IdeaForm/Disclaimers/disclaimers';
import Positions from 'buyside/components/presentation/IdeaForm/Positions/positions';
import ResearchFormWizard, {
  ResearchWizardStep
} from 'buyside/components/presentation/ResearchFormWizard/researchFormWizard';
import WriteupAndAttachments from 'buyside/components/presentation/IdeaForm/WriteupAndAttachments/writeupAndAttachments';

import {
  determineCompletedSteps,
  generateDefaultValues,
  generateValidationSchema,
  IdeaFormInput
} from 'buyside/util/researchSubmission';

import {
  IdeaSubmissionDraftFragment,
  IdeaSubmissionQuery,
  useCreateOrUpdateIdeaMutation,
  useIdeaSubmissionQuery
} from 'interfaces/graphql-typings';

export const IdeaFormContext = createContext<{
  currentContests?: IdeaSubmissionQuery['currentContests'];
  currentUser?: IdeaSubmissionQuery['currentUser'];
  ideaId?: string;
  isDraft: boolean;
  termsOfUse?: IdeaSubmissionQuery['termsOfUse'];
}>({ isDraft: true });

const steps = [
  {
    label: 'Position & Details',
    name: 'position_and_details',
    component: (): ReactElement => (
      <>
        <Positions />
        <Details />
      </>
    )
  },
  {
    label: 'Writeup & Attachments',
    name: 'writeup_and_attachments',
    component: WriteupAndAttachments
  },
  {
    label: 'Review & Submit',
    name: 'review_and_submit',
    component: Disclaimers
  }
];

const IdeaForm: FC = () => {
  const navigate = useNavigate();

  const { ideaId } = useParams();

  const [currentStep, setCurrentStep] = useState<ResearchWizardStep>(steps[0]);
  const [completedSteps, setCompletedSteps] = useState<ResearchWizardStep[]>([]);

  const methods = useForm<IdeaFormInput>({
    defaultValues: generateDefaultValues(),
    resolver: yupResolver(
      lazy(values =>
        generateValidationSchema({
          currentStepName: currentStep.name,
          isPairTrade: (values as IdeaFormInput).positions.length === 2
        })
      )
    )
  });

  const { reset } = methods;

  const handleSetCurrentStep = useCallback((step: ResearchWizardStep) => {
    window.scrollTo(0, 0);
    return setCurrentStep(step);
  }, []);

  const handleResetForm = useCallback(
    (idea?: IdeaSubmissionDraftFragment, customStep?: ResearchWizardStep) => {
      const defaultValues = generateDefaultValues(idea);
      reset(defaultValues);

      const completedStepNames = determineCompletedSteps(defaultValues);
      const { completedSteps, incompleteSteps } = steps.reduce(
        (
          memo: { completedSteps: ResearchWizardStep[]; incompleteSteps: ResearchWizardStep[] },
          step
        ) => {
          completedStepNames.includes(step.name)
            ? memo.completedSteps.push(step)
            : memo.incompleteSteps.push(step);
          return memo;
        },
        { completedSteps: [], incompleteSteps: [] }
      );
      setCompletedSteps(completedSteps);
      handleSetCurrentStep(customStep || incompleteSteps[0] || steps[steps.length - 1]);
    },
    [handleSetCurrentStep, reset]
  );

  const { data: ideaData, error, loading: ideaLoading } = useIdeaSubmissionQuery({
    onCompleted: ({ idea }) => handleResetForm(idea),
    skip: !ideaId,
    variables: { id: `${ideaId}` }
  });

  const [handleCreateOrUpdateIdea] = useCreateOrUpdateIdeaMutation();

  const { currentContests, currentUser, idea, termsOfUse } = ideaData || {};
  const { id: currentUserId, isAdmin } = currentUser || {};
  const { status, user: ideaUser } = idea || {};

  const handleCreateOrUpdateDraft = useCallback(
    (values: IdeaFormInput, nextStep: ResearchWizardStep, isSubmission: boolean) => {
      const variables = {
        ...values,
        attachments: values.attachments.map(
          ({ id, attachmentType, file, filename, researchType }) => ({
            id,
            attachmentType,
            file,
            filename,
            researchType
          })
        ),
        currentStep: currentStep.name,
        isSubmission,
        mentionedEntities: values.mentionedEntities.map(({ value }) => value)
      };

      return handleCreateOrUpdateIdea({ variables })
        .then(response => {
          const idea = response.data?.createOrUpdateIdea?.idea;
          handleResetForm(idea, nextStep);
          if (!ideaId && idea?.id) navigate(idea.id, { replace: true });
        })
        .catch(error => toast.error(error.message, { autoClose: 7500, theme: 'colored' }));
    },
    [currentStep.name, handleCreateOrUpdateIdea, handleResetForm, navigate, ideaId]
  );

  if (ideaUser && ideaUser.id !== currentUserId && !isAdmin)
    return (
      <Navigate
        to="/"
        state={{
          cannotEditError: `You cannot edit idea with ID ${ideaId}. Contact support@sumzero.com if you believe this is an error.`
        }}
      />
    );

  if (error) throw error;

  const isDraft = status === 'draft' || !idea;

  return (
    <IdeaFormContext.Provider
      value={{
        currentContests,
        currentUser,
        ideaId,
        isDraft,
        termsOfUse
      }}
    >
      <FormProvider {...methods}>
        <ResearchFormWizard
          completedSteps={completedSteps}
          currentStep={currentStep}
          handleCreateOrUpdateDraft={handleCreateOrUpdateDraft}
          handleSetCurrentStep={handleSetCurrentStep}
          isDraft={isDraft}
          isLoading={ideaLoading}
          researchPath={`/pro/research/ideas/${ideaId}`}
          researchType="Idea"
          steps={steps}
        />
      </FormProvider>
    </IdeaFormContext.Provider>
  );
};

export default IdeaForm;
